import { documentUpload } from './types/document-upload.js';
import { acknowledgment } from './types/acknowledgment.js';
import { dateCredential } from './types/date-credential.js';
import { hrRecord } from './types/hr-record.js';
import { infoTask } from './types/info-task.js';
import { candidateApplication } from './types/candidate-application.js';
import { externalProcess } from './types/external-process.js';
import { pdfForm } from './types/pdf-form.js';
import { healthChoice } from './types/health-choice.js';
/**
 * The only central file in the step engine. Adding a step type is one import and one
 * line here — nothing else in the codebase changes.
 *
 * A literal-keyed const object, not a Map or Record<string, Module>: both of those are
 * index signatures, so noUncheckedIndexedAccess adds `| undefined` to every lookup and
 * destroys the narrowing this registry exists to provide.
 */
export const stepTypes = {
    'info-task': infoTask,
    'document-upload': documentUpload,
    'candidate-application': candidateApplication,
    'hr-record': hrRecord,
    acknowledgment,
    'date-credential': dateCredential,
    'pdf-form': pdfForm,
    'health-choice': healthChoice,
    'external-process': externalProcess,
};
const _noKeyDrift = true;
void _noKeyDrift;
/** Narrowing lookup for code that knows which type it wants. */
export function getStepType(id) {
    return stepTypes[id];
}
/** The erased view, for the review queue, progress calculators and the expiry scan. */
export const allStepTypes = Object.values(stepTypes);
export const stepTypeIds = Object.keys(stepTypes);
export function isStepTypeId(value) {
    return Object.prototype.hasOwnProperty.call(stepTypes, value);
}
/** Runtime-id dispatch: returns undefined rather than throwing on an unknown id. */
export function findStepType(id) {
    return isStepTypeId(id) ? stepTypes[id] : undefined;
}
/** Validate a stored config whose type is only known from its string id. */
export function validateStepConfig(type, config) {
    const module = findStepType(type);
    if (!module)
        return { ok: false, issues: [`unknown step type: ${type}`] };
    // The erased runtime returns { type: string; config: unknown }; narrowing it back to
    // the derived union is safe because the registry guarantees key/type agreement.
    const parsed = module.runtime.validateConfig(config);
    return parsed.ok ? { ok: true, value: parsed.value } : parsed;
}
/**
 * Effects that would create evidence the readiness engine can only read as indeterminate.
 *
 * `fromCredentials` returns `impossible_dates` for a credential with no expiry against an
 * `expiry.mode: 'credential-date'` rule, so minting one leaves the person on "Needs review"
 * for ever with nothing on screen explaining why — a person who has done everything asked of
 * them. That is the same shape as the Q27 gap that stranded every registry-qualified aide,
 * and it is why this is refused at the point of approval rather than reported afterwards.
 */
function unmintableCredentials(effects, needsExpiry) {
    return effects.flatMap((effect) => effect.kind === 'mint-credential' && effect.expiresAt === null && needsExpiry(effect.requirementKey)
        ? [
            `"${effect.requirementKey}" is tracked by the expiry date on the document itself, ` +
                `so it cannot be recorded without one`,
        ]
        : []);
}
/**
 * Run a step's approval effects without knowing its concrete type.
 *
 * `needsExpiry` is REQUIRED rather than optional, and that is the point: approval is the
 * moment evidence is created, and there is no longer a way to reach it without answering
 * whether the requirement needs a date. Both callers — the HR review approve and the direct
 * record — already surface a refusal here, so the guard needs no new error path.
 */
export function approveInstance(instance, needsExpiry) {
    const module = findStepType(instance.type);
    if (!module)
        return { ok: false, issues: [`unknown step type: ${instance.type}`] };
    const effects = module.runtime.approve(instance);
    if (!effects.ok)
        return effects;
    // Checked on the EFFECTS, not on any one type's config. `collectsExpiry` and
    // `requiresExpiry` are per-type flags that can disagree with the requirement they point
    // at; what actually strands somebody is a mint-credential carrying no date, whichever type
    // produced it. This sees all of them, including types that do not exist yet.
    const problems = unmintableCredentials(effects.value, needsExpiry);
    return problems.length > 0 ? { ok: false, issues: problems } : effects;
}
//# sourceMappingURL=registry.js.map